/**
 * Operational transform over plain text — what lets several people type in one
 * task's notes at once (server/textSessions.ts, web/src/lib/collabText.ts).
 *
 * An op walks the whole old text: a positive number keeps that many characters,
 * a negative one deletes that many, a string inserts itself. Server-centric:
 * the server orders every op, so only `transform` is needed, never compose.
 * Both sides call `transform(serverOp, clientOp)`, so the server's insert goes
 * first on a tie, everywhere, and the two copies cannot disagree.
 *
 * Shared so the server and the browser run the very same arithmetic.
 */
export type TextOp = (number | string)[];

const isRetain = (c: number | string | undefined): c is number => typeof c === "number" && c > 0;
const isDelete = (c: number | string | undefined): c is number => typeof c === "number" && c < 0;
const isInsert = (c: number | string | undefined): c is string => typeof c === "string";

/** Appends to an op, merging with its last component of the same kind. */
class Builder {
  readonly op: TextOp = [];
  retain(n: number): this {
    if (n <= 0) return this;
    const last = this.op[this.op.length - 1];
    if (isRetain(last)) this.op[this.op.length - 1] = last + n;
    else this.op.push(n);
    return this;
  }
  insert(s: string): this {
    if (!s) return this;
    const last = this.op[this.op.length - 1];
    if (isInsert(last)) this.op[this.op.length - 1] = last + s;
    // Insert before a delete, so equal edits always come out the same shape.
    else if (isDelete(last)) {
      const prev = this.op[this.op.length - 2];
      if (isInsert(prev)) this.op[this.op.length - 2] = prev + s;
      else this.op.splice(this.op.length - 1, 0, s);
    } else this.op.push(s);
    return this;
  }
  delete(n: number): this {
    if (n <= 0) return this;
    const last = this.op[this.op.length - 1];
    if (isDelete(last)) this.op[this.op.length - 1] = last - n;
    else this.op.push(-n);
    return this;
  }
}

/** The length of the text an op applies to. */
export function baseLength(op: TextOp): number {
  let n = 0;
  for (const c of op) if (typeof c === "number") n += Math.abs(c);
  return n;
}

/** Whether an op off the wire is well-formed: integers and strings only. */
export function isTextOp(raw: unknown): raw is TextOp {
  return (
    Array.isArray(raw) &&
    raw.length <= 10_000 &&
    raw.every((c) => (typeof c === "number" && Number.isInteger(c) && c !== 0) || (typeof c === "string" && c.length > 0))
  );
}

/** Whether an op changes nothing. */
export function isNoop(op: TextOp): boolean {
  return op.every(isRetain);
}

/** Apply `op` to `text`. Throws when the op was made against a text of another length. */
export function applyOp(text: string, op: TextOp): string {
  if (baseLength(op) !== text.length) throw new Error(`op base ${baseLength(op)} != text length ${text.length}`);
  let out = "";
  let at = 0;
  for (const c of op) {
    if (isInsert(c)) out += c;
    else if (isRetain(c)) {
      out += text.slice(at, at + c);
      at += c;
    } else at -= c;
  }
  return out;
}

/**
 * `[a', b']` for two ops made against the same text, such that
 * `apply(apply(s, a), b') === apply(apply(s, b), a')`. On equal positions
 * `a`'s insert goes first.
 */
export function transform(a: TextOp, b: TextOp): [TextOp, TextOp] {
  if (baseLength(a) !== baseLength(b)) throw new Error("transform: ops of different base lengths");
  const a2 = new Builder();
  const b2 = new Builder();
  let i = 0;
  let j = 0;
  let x = a[i++];
  let y = b[j++];
  for (;;) {
    if (x === undefined && y === undefined) break;
    if (isInsert(x)) {
      a2.insert(x);
      b2.retain(x.length);
      x = a[i++];
      continue;
    }
    if (isInsert(y)) {
      a2.retain(y.length);
      b2.insert(y);
      y = b[j++];
      continue;
    }
    if (x === undefined || y === undefined) throw new Error("transform: ops of different base lengths");
    const n = Math.min(Math.abs(x), Math.abs(y));
    if (isRetain(x) && isRetain(y)) {
      a2.retain(n);
      b2.retain(n);
    } else if (isDelete(x) && isRetain(y)) {
      a2.delete(n);
    } else if (isRetain(x) && isDelete(y)) {
      b2.delete(n);
    }
    // Both deleted it: neither has anything left to do.
    x = shrink(x, n);
    y = shrink(y, n);
    if (x === undefined) x = a[i++];
    if (y === undefined) y = b[j++];
  }
  return [a2.op, b2.op];
}

/** A retain or delete less `n` of its length, or undefined once used up. */
function shrink(c: number, n: number): number | undefined {
  const left = c > 0 ? c - n : c + n;
  return left === 0 ? undefined : left;
}

/**
 * Where index `i` of the old text lands once `op` is applied. An insert
 * exactly at `i` pushes it along (`pushed`), so someone else's caret stays
 * after what was typed in front of it; your own caret is not pushed by
 * someone else's typing at it.
 */
export function transformIndex(i: number, op: TextOp, pushed = true): number {
  let out = i;
  let at = 0;
  for (const c of op) {
    if (at > i) break;
    if (isRetain(c)) at += c;
    else if (isInsert(c)) {
      if (at < i || pushed) out += c.length;
    }
    else {
      out -= Math.min(-c, i - at);
      at -= c;
    }
  }
  return out;
}

/**
 * The op that turns `before` into `after`, as one splice. `caret` is where the
 * caret ended up, which places the edit when the text alone is ambiguous:
 * typing an "a" into "aa" could be at any of three places.
 */
export function diffOp(before: string, after: string, caret?: number): TextOp {
  let prefix = 0;
  const max = Math.min(before.length, after.length);
  while (prefix < max && before[prefix] === after[prefix]) prefix++;
  if (caret !== undefined) prefix = Math.min(prefix, Math.max(0, caret - Math.max(0, after.length - before.length)));
  let suffix = 0;
  while (
    suffix < max - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) suffix++;
  return new Builder()
    .retain(prefix)
    .insert(after.slice(prefix, after.length - suffix))
    .delete(before.length - prefix - suffix)
    .retain(suffix).op;
}
